import cookie from 'cookie';
import type { ParsedUrlQuery } from 'node:querystring';

import { PUBLIC_URL } from '@/lib/const';
import { isUsableGlobalECartId } from '@/lib/globalECartId';
import { buildGlobalEDataCookie } from '@/lib/globalEData';
import { paths } from '@/lib/paths';
import { getRegionFromLocale } from '@/lib/regions';
import type { StorageHelper } from '@/lib/storage';
import { GLOBAL_ECART_ID_MAX_AGE } from '@/lib/storage';

export const GLOBAL_E_PAY_PER_LINK_QUERY_PARAM = 'payPerLink';
export const GLOBAL_E_PAY_PER_LINK_ORDER_ID_COOKIE = 'GlobalEPayPerLinkOrderId';
const GLOBAL_E_PAYMENT_RETURN_QUERY_PARAM = 'cb';

const getPayPerLinkCookieDomain = () => `.${new URL(PUBLIC_URL).hostname}`;

export const buildGlobalEPayPerLinkRedirect = (
  orderId: string,
  locale: string,
) => {
  const { country, locale: regionLocale } = getRegionFromLocale(locale);
  const cookieOptions = {
    secure: process.env.NODE_ENV === 'production',
    path: '/',
    domain: getPayPerLinkCookieDomain(),
  };
  const cartCookieOptions = {
    ...cookieOptions,
    maxAge: GLOBAL_ECART_ID_MAX_AGE,
  };

  return {
    cookies: [
      cookie.serialize('GlobalECartId', orderId, cartCookieOptions),
      cookie.serialize(
        GLOBAL_E_PAY_PER_LINK_ORDER_ID_COOKIE,
        orderId,
        cartCookieOptions,
      ),
      cookie.serialize(
        'GlobalE_Data',
        JSON.stringify(buildGlobalEDataCookie(country.code, country.currency)),
        cookieOptions,
      ),
      cookie.serialize('GlobalE_Locale', regionLocale.locale, cookieOptions),
    ],
    destination: `/${locale}${paths.store['global-e'].checkout.asPath({
      query: { [GLOBAL_E_PAY_PER_LINK_QUERY_PARAM]: 1 },
    })}`,
  };
};

export const readGlobalEPayPerLinkOrderId = (
  storageHelper: StorageHelper,
): string | undefined => {
  const orderId = storageHelper.getCookie(
    GLOBAL_E_PAY_PER_LINK_ORDER_ID_COOKIE,
  );

  return isUsableGlobalECartId(orderId) ? orderId : undefined;
};

// Every external payment step returns to the plain checkout URL with
// Global-e's ExternalPaymentsTriggers (token, cb, ts).
export const detectGlobalEPayPerLinkCheckout = (
  query: ParsedUrlQuery | undefined,
  storageHelper: StorageHelper,
): boolean =>
  query?.[GLOBAL_E_PAY_PER_LINK_QUERY_PARAM] === '1' ||
  (query?.[GLOBAL_E_PAYMENT_RETURN_QUERY_PARAM] !== undefined &&
    readGlobalEPayPerLinkOrderId(storageHelper) !== undefined);

export const clearGlobalEPayPerLinkOrderId = (
  storageHelper: StorageHelper,
): void => {
  storageHelper.removeCookie(GLOBAL_E_PAY_PER_LINK_ORDER_ID_COOKIE, {
    domain: getPayPerLinkCookieDomain(),
    path: '/',
  });
};
